import { getCurrentUser } from "./firebaseAuth";
import { AWSCredentialManager } from "./awsCredentialManager";
import { User as FirebaseUser } from "firebase/auth";

/**
 * Bridge Firebase authentication with AWS credentials for S3 access
 * This utility helps us use Firebase auth while accessing AWS services with IAM user credentials
 */

interface AmplifyCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
  region: string;
}

/**
 * Get AWS credentials based on the current Firebase authentication state
 * This function uses the new credential manager to select appropriate credentials
 */
export async function getAmplifyCredentials(): Promise<AmplifyCredentials | null> {
  try {
    // Check Firebase authentication first
    let firebaseUser = getCurrentUser();
    let isAuthenticated = firebaseUser !== null;
    let userEmail = firebaseUser?.email || '';
    
    // If no Firebase user, check for persisted AWS Cognito auth
    if (!isAuthenticated) {
      try {
        const persistedUser = localStorage.getItem('aws-cognito-user');
        if (persistedUser) {
          const userData = JSON.parse(persistedUser);
          if (userData.uid) {
            isAuthenticated = true;
            userEmail = userData.email || '';
            // Set the Firebase user to the persisted user to ensure Firebase auth state is consistent
            firebaseUser = userData as FirebaseUser;
            console.log("Using AWS Cognito credentials for:", userEmail);
          }
        }
      } catch (error) {
        console.warn("Failed to check persisted AWS Cognito auth:", error);
      }
    } else {
      console.log("Using Firebase credentials for:", userEmail);
    }
    
    console.log("Credential selection - isAuthenticated:", isAuthenticated);

    // Use credential manager to get appropriate credentials
    const credentials =
      AWSCredentialManager.getCredentialsForUser(isAuthenticated);

    return {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      region: credentials.region,
      // No sessionToken needed for IAM user credentials
    };
  } catch (error) {
    console.error("Error getting AWS credentials:", error);
    
    // Fallback to unauthenticated credentials if there's an error
    try {
      console.log("Falling back to unauthenticated credentials");
      const fallbackCredentials = AWSCredentialManager.getUnauthCredentials();
      return {
        accessKeyId: fallbackCredentials.accessKeyId,
        secretAccessKey: fallbackCredentials.secretAccessKey,
        region: fallbackCredentials.region,
      };
    } catch (fallbackError) {
      console.error("Fallback credential retrieval failed:", fallbackError);
      return null;
    }
  }
}

/**
 * Check if the current user has valid AWS credentials
 */
export async function hasValidCredentials(): Promise<boolean> {
  try {
    const credentials = await getAmplifyCredentials();
    return credentials !== null;
  } catch (error) {
    console.error("Error checking credentials:", error);
    return false;
  }
}

/**
 * Initialize the Firebase-Amplify bridge
 * This should be called when the app initializes
 */
export function initializeAmplifyFirebaseBridge(): void {
  // Set up listeners for Firebase auth state changes
  // to refresh Amplify credentials when needed

  console.log("Firebase-Amplify bridge initialized");

  // Validate S3 configuration and security in development mode
  if (process.env.NODE_ENV === "development") {
    // Import and run configuration validation (lazy import to avoid circular dependencies)
    import("./s3ConfigValidator")
      .then(({ validateS3Configuration, logValidationResults }) => {
        validateS3Configuration().then((results) => {
          logValidationResults(results);

          // If configuration is valid, run security validation
          if (results.isValid) {
            console.log(
              "🔒 Configuration valid, running security validation..."
            );

            // Run security validation after a short delay to allow auth to settle
            setTimeout(() => {
              import("./s3SecurityValidator").then(
                ({ S3SecurityValidator }) => {
                  const validator = new S3SecurityValidator();
                  validator
                    .runSecurityValidation()
                    .then((report) => {
                      console.log(
                        S3SecurityValidator.formatSecurityReport(report)
                      );

                      if (!report.overallPassed) {
                        console.warn(
                          "⚠️ Security validation failed. Review the report above."
                        );
                      }
                    })
                    .catch((error) => {
                      console.warn("Could not run security validation:", error);
                    });
                }
              );
            }, 2000); // 2 second delay
          }
        });
      })
      .catch((error) => {
        console.warn("Could not validate S3 configuration:", error);
      });
  }

  // TODO: In production, implement custom auth provider that:
  // 1. Listens to Firebase auth state changes
  // 2. Exchanges Firebase ID tokens for AWS credentials via your backend
  // 3. Updates Amplify session with new credentials
  // 4. Handles token refresh automatically
}

export default {
  getAmplifyCredentials,
  hasValidCredentials,
  initializeAmplifyFirebaseBridge,
};
